import type { CurrentUserQuery } from '@/graphql/queries/currentUserQuery.gql.generated'
import * as ApolloProvider from '@/integrations/apollo/root-provider'
import { CurrentUserDocument } from '@/graphql/queries/currentUserQuery.gql.generated'
import { env } from '@/env'
import { getAppProviderContext } from '@/app/providers'
import { decorateCurrentUserPermissions } from '@/shared/lib/workspace-permissions'
import { DocumentRecordClient } from '@/integrations/zustand-store/document-record-client'
import { isCustomerAuthError } from '@/shared/lib/customer-auth'
import { getLegalAccessState } from '@/features/legal/legal-access'

export async function bootstrapApp() {
  if (typeof window !== 'undefined' && window.location.pathname === '/login') {
    return { authenticated: false as const }
  }

  const legalStartup = await getLegalStartup()
  if (legalStartup) return legalStartup

  const apollo = ApolloProvider.getContext()

  let currentUser: CurrentUserQuery['currentUser'] | null

  try {
    const result = await apollo.apolloClient.query<CurrentUserQuery>({
      query: CurrentUserDocument,
    })
    currentUser = result.data?.currentUser ?? null
  } catch (error) {
    if (!isCustomerAuthError(error)) {
      throw error
    }

    return { authenticated: false as const }
  }

  if (!currentUser) {
    return { authenticated: false as const }
  }

  const workspaceId = currentUser.workspaceId

  if (!workspaceId) {
    throw new Error(
      'Unable to initialize records: no active workspace configured',
    )
  }

  const records = new DocumentRecordClient(
    apollo.apolloClient,
    workspaceId,
    env.VITE_API_URL,
  )

  const appProviders = getAppProviderContext({
    records,
    client: apollo.apolloClient,
  })

  return {
    authenticated: true as const,
    records,
    currentUser: decorateCurrentUserPermissions(currentUser),
    providers: appProviders,
  }
}

async function getLegalStartup() {
  const state = await getLegalAccessState()
  if (state === null) return { authenticated: false as const }
  if (state.mode === 'pending') {
    return { authenticated: false as const, legalState: state }
  }

  return null
}
